import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import RCore from 'RCore';
import _ from 'lodash';
import cx from 'classnames';
import UI from 'UI';
import SMTeamLogo from '../SMTeamLogo';
import eventify from 'Eventify';
import DateFormatted from '../DateFormatted';

//These lines are needed to get common i18
const i18Common = require('../../../../i18/i18.Common');
const i18 = RCore.getTranslation.bind({ dict: i18Common });

const StatsFormWrapper = styled.div`
    text-transform: uppercase;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${props => props.theme.statTableBgColor};
    color: ${props => props.theme.statTableTextColor};
    margin-top: 0px;
    margin-bottom: 4px;
    padding: 16px 0;

    .title {
        font-size: 14px;
        color: ${props => props.theme.statTableLabelTextColor};
        min-width: 250px;
        text-align: center;
    }
`;

const MatchHeader = styled.div`
    padding: 20px 0;
    background-color: ${props => props.theme.content.backgroundColor};
    color: ${props => props.theme.color};
    display: flex;
    align-items: center;

    & > div {
        flex-grow: 1;
    }
`;

const MatchHeaderLarge = styled.div`
    color: ${props => props.theme.color};
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1px;
`;

const MatchHeaderMin = MatchHeader.extend`
    display: block;

    .devider {
        font-size: 75px;
        line-height: 1em;
        font-weight: 900;
        transform: translateY(-13px);
        margin: 0 30px;
    }

    .teams-wrapper {
        display: flex;
        align-items: center;
    }

    .score-wrapper {
        > div {
            width: auto;
        }
    }
`;

const Team = styled.div`
    width: 50%;
    display: flex;
    align-items: center;
    justify-content: ${props => (props.side === 'home' ? 'flex-end' : 'flex-start')};

    .wdp-team-logo {
        width: 80px;
        height: 80px;
        margin: 0;
        img {
            height: 100%;
            width: auto;
        }
    }

    .name {
        height: 80px;
        margin: 0;
        white-space: nowrap;
        & > * {
            vertical-align: middle;
        }
    }

    .team-name {
        padding: 0;
        min-width: 110px;
        text-align: ${props => (props.side === 'home' ? 'right' : 'left')};
        margin: ${props => (props.side === 'home' ? '0 15px 0 0' : '0 0 0 15px')};
        font-size: 60px;
        font-weight: 700;
        line-height: 65px;
        height: 65px;
        display: inline-block;
        position: relative;
    }

    .rank {
        position: absolute;
        bottom: 0;
        right: 0;
        left: 0;
        font-weight: normal;
        transform: translateY(100%);
        font-size: 15px;
        line-height: 15px;
        text-transform: lowercase;
    }
`;

const StartInfo = styled.div`
    text-align: center;
    white-space: nowrap;

    width: 300px;

    .time {
        font-size: 38px;
        line-height: 40px;
        font-weight: 700;
        margin-top: 4px;
        margin-bottom: 4px;
    }

    .tournament {
        overflow: hidden;
        max-width: 130px;
        margin: 0 auto;
        text-transform: uppercase;
        font-size: 16px;
        line-height: 18px;
        font-weight: 700;

        svg {
            height: 13px;

            color: ${props => props.theme.color};
            fill: ${props => props.theme.color};
        }
    }

    .channel {
        font-size: 16px;
        line-height: 18px;
        white-space: nowrap;
    }
`;

const LiveLabel = styled.div`
    font-size: 20px;
    font-weight: 700;
    line-height: 22px;
    padding: 3px 10px;
    margin-bottom: 10px;
    background: ${props => props.theme.primaryColor};
    color: ${props => props.theme.primaryTextColor};
    text-align: center;
    text-transform: uppercase;
    min-width: 90px;
    display: inline-block;
`;

const LiveLabelMin = LiveLabel.extend`
    font-size: 1.5rem;
    margin-bottom: 0;
    background: none;
`;

const FinishedLabel = LiveLabel.extend``;

const FinishedLabelMin = LiveLabelMin.extend``;

const Score = styled.div`
    font-size: 32px;
    line-height: 34px;
    font-weight: 700;
    min-width: 250px;
    white-space: nowrap;
    text-align: center;

    .nums {
        font-size: 80px;
        line-height: 1em;
        font-weight: normal;

        .better {
            font-weight: bold;
        }
    }
`;

const ScoreMin = Score.extend`
    width: 100%;
    font-size: 1.5rem;
    margin-bottom: 30px;
`;

const StatsForm = styled.ul`
    padding: 0;
    margin: 0;
    flex-grow: 1;
    flex-basis: 0;
    text-align: ${({ align }) => align};

    ${({ align }) => (align === 'left' ? 'transform: translateX(-8px);' : 'transform: translateX(8px);')};
`;

const StatItem = styled.li`
    background-color: ${props => props.theme.wdl[props.result]};
    color: #fff;
    border-radius: 50%;
    display: inline-block;
    vertical-align: middle;
    width: 24px;
    height: 24px;
    margin: 0 8px 4px;
    font-weight: 700;
    text-align: center;
    line-height: 24px;
    font-size: 12px;
    text-transform: uppercase;
`;

const StatRows = styled.div``;

const StatRow = styled.div`
    display: flex;
    align-items: center;
    margin-top: 4px;
    background-color: ${props => props.theme.statTableBgColor};
    padding: 17px 10px 16px;

    .val {
        min-width: 50px;
        font-size: 30px;
        line-height: 1em;
        flex-grow: 1;
        flex-basis: 0;
        color: ${props => props.theme.statTableTextColor};

        &.val_better {
            font-weight: 700;
            font-size: 30px;
        }

        &.tar {
            text-align: right;
        }
    }

    .label {
        color: ${props => props.theme.statTableLabelTextColor};
        flex-grow: 1;
        font-size: 18px;
        line-height: 1.5;
        text-align: center;
        width: 1px;
        padding: 0 5px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        text-transform: uppercase;
        min-width: 250px;
        ${({ onlyLabel }) => onlyLabel && 'position: static !important; transform: translate(0, 0) !important;'};
        ${({ labelLength }) => labelLength >= 19 && 'white-space: normal !important;'};
    }

    .h2h_stats__chart {
        flex-grow: 1.5;
    }

    .h2h_stats__chart ~ .h2h_stats__val-wrapper.tar {
        padding-right: 30px;

        .h2h_stats__val {
            width: auto;
        }
    }

    .h2h_stats__chart ~ .h2h_stats__val-wrapper.tal {
        padding-left: 30px;

        .h2h_stats__val {
            width: auto;
        }
    }
`;

const StatRowLarge = StatRow.extend`
    background: none;
    padding: 32px 10px;

    .label {
        font-size: 28px;
        line-height: 1em;
    }

    .val,
    .val.val_better,
    .val.tar {
        font-size: 60px;
        line-height: 1em;
        text-align: center;

        strong {
            font-size: 75px;
            line-height: 1em;
        }
    }
`;

const Chart = styled.div`
    width: 100%;
    height: 30px;
    position: relative;

    &:after {
        content: '';
        position: absolute;
        left: ${props => (props.dir === 'left' ? 0 : 'auto')};
        right: ${props => (props.dir === 'right' ? 0 : 'auto')};
        top: 0;
        bottom: 0;
        width: ${props => props.val}%;
        background-color: ${props => (props.color === 'home' ? props.theme.statColorHome : props.theme.statColorAway)};
    }
`;

const LatestH2HForm = styled.div`
    text-align: center;
    text-transform: uppercase;
    padding: 25px 30px;
    margin-top: 4px;
    color: ${props => props.theme.statTableTextColor};
    background-color: ${props => props.theme.statTableBgColor};

    .title {
        font-weight: 400;
        font-size: 14px;
        margin-bottom: 15px;
        color: ${props => props.theme.statTableLabelTextColor};
    }

    .form {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: ${props => props.theme.statTableLabelTextColor};

        > div {
            font-weight: bold;

            span {
                background-color: transparent !important;
            }
        }
    }
`;

const Cell = styled.div`
    height: 30px;
    line-height: 28px;
    flex-basis: ${props => props.flexBasis}%;
    white-space: nowrap;
    min-width: 74px;
    box-sizing: border-box;
    padding: 0 20px;
    font-size: 0.875rem;

    &:nth-child(1) {
        color: ${props => props.theme.statTextColor};
        background-color: ${props => props.theme.statColorHome};
        text-align: left;
    }

    &:nth-child(2) {
        color: ${props => props.theme.statTextColor};
        background-color: ${props => props.theme.statColorNeutral || props.theme.content.statTableBgColor};
        padding: 0;
    }

    &:nth-last-of-type(1) {
        color: ${props => props.theme.statTextColor};
        background-color: ${props => props.theme.statColorAway};
        text-align: right;
    }
`;

const TeamLarge = styled.div`
    width: 300px;
    min-height: 300px;
    align-items: center;
    justify-content: center;
    display: flex;
    background-color: ${({ theme }) => theme.content.backgroundColor};

    .wdp-team-logo {
        width: 150px;
        margin: 0;

        img {
            height: 100%;
            width: auto;
        }
    }

    .name {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .team-name {
        font-size: 50px;
        font-weight: bold;
        line-height: 1em;
        margin-top: 20px;
    }
`;

const Center = styled.div`
    min-height: 300px;
    align-items: center;
    justify-content: center;
    display: flex;
    flex-grow: 1;
    margin: 0 5px;
    background-color: ${({ theme }) => theme.content.backgroundColor};
`;

const LiveLabelLarge = styled.div`
    font-size: 22px;
    font-weight: 700;
    line-height: 22px;
    padding: 3px 10px;
    margin-bottom: 10px;
    color: ${props => props.theme.primaryTextColor};
    text-align: center;
    text-transform: uppercase;
    min-width: 90px;
    display: inline-block;
    background: none;
`;

const Header = ({ event, rankInfo, status }, ctx) => {
    const lang = _.get(ctx, 'info.lang', 'en');
    event = eventify(event);
    const { statusType, statusFull } = event;
    const isLive = statusType === 'inprogress';
    const isNotStarted = statusType === 'notstarted';
    const isFinished = statusFull === 'Finished';

    //ToDo: need to change in api
    const ttName =
        event.tournamentName &&
            (event.tournamentName.toLowerCase() === 'superliga' || event.tournamentName.toLowerCase() === 'superleague')
            ? `3F ${event.tournamentName}`
            : event.tournamentName || '';

    return (
        <MatchHeader>
            <Team side="home">
                <div className="name">
                    <span className="team-name">
                        <span />
                        <span>{event.homeShortName}</span>
                        {rankInfo && rankInfo.home && (
                            <span className="rank">
                                <b>#{rankInfo.home.rank}</b> ({rankInfo.home.points} {i18('points', lang)})
                            </span>
                        )}
                    </span>
                    <SMTeamLogo tournamentId={event.tournamentTemplateId} teamId={event.homeId} />
                </div>
            </Team>

            {isNotStarted && (
                <StartInfo>
                    <div className="tournament">
                        <UI.TournamentName name={ttName} id={event.tournamentTemplateId} />
                    </div>
                    <DateFormatted className="time" el="span" date={event.startDate} type="TIME" />
                    {event.channel && <div className="channel">{event.channel.name}</div>}
                </StartInfo>
            )}

            {(isLive || isFinished) && (
                <Score>
                    {isLive && (
                        <LiveLabel>
                            {i18('live', lang)} {event.isHT() ? i18('ht', lang) : `${event.getMinutes()}'`}
                        </LiveLabel>
                    )}
                    {isFinished && <FinishedLabel>{i18('finished', lang)}</FinishedLabel>}
                    <div>
                        {_.get(event, 'score.home')} - {_.get(event, 'score.away')}
                    </div>
                </Score>
            )}

            <Team side="away">
                <div className="name">
                    <SMTeamLogo tournamentId={event.tournamentTemplateId} display="logo" teamId={event.awayId} />
                    <span className="team-name">
                        <span>{event.awayShortName}</span>

                        {rankInfo && rankInfo.away && (
                            <span className="rank">
                                <b>#{rankInfo.away.rank}</b> ({rankInfo.away.points} {i18('points', lang)})
                            </span>
                        )}
                    </span>
                </div>
            </Team>
        </MatchHeader>
    );
};

const HeaderLarge = ({ event, rankInfo, status }, ctx) => {
    const lang = _.get(ctx, 'info.lang', 'en');
    event = eventify(event);
    const { statusType, statusFull } = event;
    const isLive = statusType === 'inprogress';
    const isNotStarted = statusType === 'notstarted';
    const isFinished = statusFull === 'Finished';

    //ToDo: need to change in api
    const ttName =
        event.tournamentName &&
            (event.tournamentName.toLowerCase() === 'superliga' || event.tournamentName.toLowerCase() === 'superleague')
            ? `3F ${event.tournamentName}`
            : event.tournamentName || '';

    const homeScore = _.get(event, 'score.home', 0);
    const awayScore = _.get(event, 'score.away', 0);
    const homeIsBetter = homeScore > awayScore;
    const awayIsBetter = awayScore > homeScore;

    return (
        <MatchHeaderLarge>
            <TeamLarge side="home">
                <div className="name">
                    <SMTeamLogo tournamentId={event.tournamentTemplateId} teamId={event.homeId} />
                    <span className="team-name">
                        <span />
                        <span>{event.homeShortName}</span>
                        {rankInfo && rankInfo.home && (
                            <span className="rank">
                                <b>#{rankInfo.home.rank}</b> ({rankInfo.home.points} {i18('points', lang)})
                            </span>
                        )}
                    </span>
                </div>
            </TeamLarge>

            <Center>
                {isNotStarted && (
                    <StartInfo>
                        <div className="tournament">
                            <UI.TournamentName name={ttName} id={event.tournamentTemplateId} />
                        </div>
                        <DateFormatted className="time" el="span" date={event.startDate} type="TIME" />
                        {event.channel && <div className="channel">{event.channel.name}</div>}
                    </StartInfo>
                )}

                {(isLive || isFinished) && (
                    <Score>
                        {isLive && (
                            <LiveLabelLarge>
                                {i18('live', lang)} {event.isHT() ? i18('ht', lang) : `${event.getMinutes()}'`}
                            </LiveLabelLarge>
                        )}
                        {isFinished && <LiveLabelLarge>{i18('finished', lang)}</LiveLabelLarge>}
                        <div className="nums">
                            <span className={`${homeIsBetter ? 'better' : ''}`}>{homeScore}</span> :{' '}
                            <span className={`${awayIsBetter ? 'better' : ''}`}>{awayScore}</span>
                        </div>
                    </Score>
                )}
            </Center>

            <TeamLarge side="away">
                <div className="name">
                    <SMTeamLogo tournamentId={event.tournamentTemplateId} display="logo" teamId={event.awayId} />
                    <span className="team-name">
                        <span>{event.awayShortName}</span>

                        {rankInfo && rankInfo.away && (
                            <span className="rank">
                                <b>#{rankInfo.away.rank}</b> ({rankInfo.away.points} {i18('points', lang)})
                            </span>
                        )}
                    </span>
                </div>
            </TeamLarge>
        </MatchHeaderLarge>
    );
};

const HeaderMinimized = ({ event, rankInfo }, ctx) => {
    const lang = _.get(ctx, 'info.lang', 'en');
    event = eventify(event);
    const { statusType, statusFull } = event;
    const isLive = statusType === 'inprogress';
    const isNotStarted = statusType === 'notstarted';
    const isFinished = statusFull === 'Finished';

    return (
        <MatchHeaderMin>
            <div className="score-wrapper">
                {isNotStarted && (
                    <StartInfo>
                        <div className="tournament">
                            <UI.TournamentName name={event.tournamentName} id={event.tournamentTemplateId} />
                        </div>
                        <DateFormatted className="time" el="div" date={event.startDate} type="TIME" />
                        {event.channel && <div className="channel">{event.channel.name}</div>}
                    </StartInfo>
                )}

                {(isLive || isFinished) && (
                    <ScoreMin>
                        {isLive && (
                            <LiveLabelMin>
                                {i18('live', lang)} {event.isHT() ? i18('ht', lang) : `${event.getMinutes()}'`}
                            </LiveLabelMin>
                        )}
                        {isFinished && <FinishedLabelMin>{i18('finished', lang)}</FinishedLabelMin>}
                        <div>
                            {_.get(event, 'score.home')}:{_.get(event, 'score.away')}
                        </div>
                    </ScoreMin>
                )}
            </div>

            <div className="teams-wrapper">
                <Team side="home">
                    <div className="name">
                        <div className="name-wrapper">
                            <span className="team-name">{event.homeShortName}</span>
                            {rankInfo && rankInfo.home && (
                                <span className="rank">
                                    <b>#{rankInfo.home.rank}</b> ({rankInfo.home.points} {i18('points', lang)})
                                </span>
                            )}
                        </div>
                    </div>
                </Team>
                <span className="devider">-</span>
                <Team side="away">
                    <div className="name">
                        <div className="name-wrapper">
                            <span className="team-name">{event.awayShortName}</span>
                            {rankInfo && rankInfo.away && (
                                <span className="rank">
                                    <b>#{rankInfo.away.rank}</b> ({rankInfo.away.points} {i18('points', lang)})
                                </span>
                            )}
                        </div>
                    </div>
                </Team>
            </div>
        </MatchHeaderMin>
    );
};

const LatestFormRow = ({ form, limit, title, clean }, ctx) => {
    const lang = _.get(ctx, 'info.lang', 'en');

    let latestHomeForm = form.home.short_results || [];
    let latestAwayForm = form.away.short_results || [];

    const latestHomeFormCount = latestHomeForm.length;
    const latestAwayFormCount = latestAwayForm.length;

    const notPlayedHomeGames =
        latestHomeFormCount < limit ? new Array(limit - latestHomeFormCount).fill({ result: 'notPlayed' }) : null;
    const notPlayedAwayGames =
        latestAwayFormCount < limit ? new Array(limit - latestAwayFormCount).fill({ result: 'notPlayed' }) : null;

    if (notPlayedHomeGames) latestHomeForm = [...notPlayedHomeGames, ...latestHomeForm];
    if (notPlayedAwayGames) latestAwayForm = [...notPlayedAwayGames, ...latestAwayForm];

    return (
        <StatsFormWrapper className="__c__b3_form">
            <StatsForm align="right">
                {latestHomeForm.map((r, i) => {
                    return (
                        <StatItem key={i} result={r.result} className={`stat-item stat-item_${r.result}`}>
                            {clean ? null : i18(`${r.result}.short`, lang)}
                        </StatItem>
                    );
                })}
            </StatsForm>

            <span className="title __c__cformtitle">{title}</span>

            <StatsForm align="left">
                {latestAwayForm.map((r, i) => {
                    return (
                        <StatItem key={i} result={r.result} className={`stat-item stat-item_${r.result}`}>
                            {clean ? null : i18(`${r.result}.short`, lang)}
                        </StatItem>
                    );
                })}
            </StatsForm>
        </StatsFormWrapper>
    );
};

const Rows = ({ dataset }) => {
    return (
        <StatRows>
            {dataset.map(
                ({
                    label,
                    home,
                    away,
                    postfix = '',
                    prefix = '',
                    sum,
                    chart = true,
                    lessIsBetter = false,
                    toFixed = false,
                    displayChart = false,
                }) => {
                    if (!sum) sum = home + away;
                    let homeIsBetter = home > away;
                    let awayIsBetter = away > home;

                    if (lessIsBetter) homeIsBetter = !homeIsBetter;
                    if (lessIsBetter) awayIsBetter = !awayIsBetter;

                    return (
                        <StatRow
                            key={label}
                            className="stat-row"
                            onlyLabel={home == null && away == null}
                            labelLength={label.length}
                        >
                            {displayChart && (
                                <div className="tal h2h_stats__chart">
                                    {chart && <Chart val={(home / sum) * 100} dir="left" color="home" />}
                                </div>
                            )}

                            {home != null && (
                                <div className={cx('tar val __c__c1_style1', { val_better: homeIsBetter })}>
                                    {prefix}
                                    {Number.isInteger(home) ? home : home.toFixed(toFixed)}
                                    {postfix}
                                </div>
                            )}

                            <div className="label __c__c2_style1">{label}</div>

                            {away != null && (
                                <div className={cx('val __c__c3_style1', { val_better: awayIsBetter })}>
                                    {prefix}
                                    {Number.isInteger(away) ? away : away.toFixed(toFixed)}
                                    {postfix}
                                </div>
                            )}

                            {displayChart && (
                                <div className="tar h2h_stats__chart">
                                    {chart && <Chart val={(away / sum) * 100} dir="right" color="away" />}
                                </div>
                            )}
                        </StatRow>
                    );
                },
            )}
        </StatRows>
    );
};

const getFormattedNumber = num => {
    if (!num) {
        return <div>-</div>;
    }

    const fixNum = Number(num).toFixed(1);
    const firstSign = `${fixNum}`.charAt(0);
    const rest = `${fixNum}`.slice(1);

    return (
        <div>
            <strong>{firstSign}</strong>
            {rest}!
        </div>
    );
};

const RowsLarge = ({ dataset }) => {
    return (
        <StatRows>
            {dataset.map(
                ({
                    label,
                    home,
                    away,
                    postfix = '',
                    prefix = '',
                    sum,
                    chart = true,
                    lessIsBetter = false,
                    toFixed = false,
                    displayChart = false,
                    largeSign = false,
                }) => {
                    if (!sum) sum = home + away;
                    let homeIsBetter = home > away;
                    let awayIsBetter = away > home;

                    if (lessIsBetter) homeIsBetter = !homeIsBetter;
                    if (lessIsBetter) awayIsBetter = !awayIsBetter;

                    return (
                        <StatRowLarge
                            key={label}
                            className="stat-row"
                            onlyLabel={home == null && away == null}
                            labelLength={label.length}
                        >
                            {displayChart && (
                                <div className="tal h2h_stats__chart">
                                    {chart && <Chart val={(home / sum) * 100} dir="left" color="home" />}
                                </div>
                            )}

                            {home != null && (
                                <div className={cx('tar val __c__c1_style1', { val_better: homeIsBetter })}>
                                    {prefix}
                                    {largeSign ? getFormattedNumber(home) : home}
                                    {/*{Number.isInteger(home) ? home : home.toFixed(toFixed)}*/}
                                    {postfix}
                                </div>
                            )}

                            <div className="label __c__c2_style1">{label}</div>

                            {away != null && (
                                <div className={cx('val __c__c3_style1', { val_better: awayIsBetter })}>
                                    {prefix}
                                    {largeSign ? getFormattedNumber(away) : away}
                                    {/*{Number.isInteger(away) ? away : away.toFixed(toFixed)}*/}
                                    {postfix}
                                </div>
                            )}

                            {displayChart && (
                                <div className="tar h2h_stats__chart">
                                    {chart && <Chart val={(away / sum) * 100} dir="right" color="away" />}
                                </div>
                            )}
                        </StatRowLarge>
                    );
                },
            )}
        </StatRows>
    );
};

const H2HRow = ({ event, h2h, h2hTitle }) => {
    if (!h2h.homeTeam) return <div />;

    const cells = [
        {
            name: event.homeShortName,
            value: h2h.homeTeam.wins,
        },
        {
            name: h2hTitle.draw,
            value: h2h.draws,
        },
        {
            name: event.awayShortName,
            value: h2h.awayTeam.wins,
        },
    ];

    const numOfMatches = h2h.homeTeam.wins + h2h.draws + h2h.awayTeam.wins;

    return (
        <LatestH2HForm>
            <div className="title __c__c5_style1">
                {numOfMatches} {h2hTitle.title}
            </div>
            <div className="form">
                {cells.map((cell, i) => (
                    <Cell
                        key={cell.name + cell.value}
                        flexBasis={cell.value * (100 / numOfMatches)}
                        className={`__c__b${i}_style1`}
                    >
                        <span className={`__c__c${i}_style2`}>
                            {cell.name}: {cell.value}
                        </span>
                    </Cell>
                ))}
            </div>
        </LatestH2HForm>
    );
};

Header.contextTypes = {
    info: PropTypes.object,
};

HeaderLarge.contextTypes = {
    info: PropTypes.object,
};

HeaderMinimized.contextTypes = {
    info: PropTypes.object,
};

LatestFormRow.contextTypes = {
    info: PropTypes.object,
    moment: PropTypes.func,
};

Rows.contextTypes = {
    info: PropTypes.object,
    moment: PropTypes.func,
};

RowsLarge.contextTypes = {
    info: PropTypes.object,
    moment: PropTypes.func,
};

H2HRow.contextTypes = {
    info: PropTypes.object,
    moment: PropTypes.func,
};

const Container = ({ children }) => <div>{children}</div>;

export default { Container, Header, HeaderMinimized, HeaderLarge, LatestFormRow, Rows, RowsLarge, H2HRow };
export { Container, Header, HeaderMinimized, HeaderLarge, LatestFormRow, Rows, RowsLarge, H2HRow };



/** WEBPACK FOOTER **
 ** ../src/widgets/components/sm/views/h2h/H2HTableSimple.js
 **/